import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trophy, Loader2, TrendingUp, Award, ArrowRight } from "lucide-react";

/**
 * Workshop floor leaderboard — top advisors this week.
 *
 * Why this exists: it's the only widget on the manager dashboard that drives
 * adoption from below. When a service advisor sees their name in lights
 * ("James — 78% · £4,250 recovered"), conversion behaviour improves WITHOUT
 * an explicit incentive scheme. Directors specifically ask "will my team
 * actually use it?" and a visible leaderboard is the clearest answer.
 *
 * UX:
 *   - 7-day default window (toggle to 30 / 90 day)
 *   - Top row gets a gold trophy; #2 silver; #3 bronze
 *   - Empty state handled — shows during a brand-new workshop's first week
 *   - All amounts net-of-commission (we surface what the dealer keeps)
 */

const PERIODS = [
  { id: 7,  label: "7d" },
  { id: 30, label: "30d" },
  { id: 90, label: "90d" },
];

const RANK_BG = ["bg-yellow-500/20 text-yellow-200", "bg-zinc-400/20 text-zinc-300", "bg-amber-700/30 text-amber-200"];

function gbp(n) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency", currency: "GBP",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(Math.max(0, n || 0));
}

export default function LeaderboardWidget() {
  const [period, setPeriod] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/analytics/leaderboard", { params: { days: period } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Couldn't load leaderboard"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  const rows = (data?.rows || []).filter(r => r.jobs_sent > 0).slice(0, 6);

  return (
    <section className="card-default p-5" data-testid="leaderboard-widget">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-primary" />
          <h2 className="font-display font-bold text-base">Top closers</h2>
          <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
            this {period === 7 ? "week" : period === 30 ? "month" : "quarter"}
          </span>
        </div>
        <div className="flex gap-1 rounded-md border border-border p-0.5">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className={`px-2.5 py-1 text-[11px] font-bold rounded transition-colors ${
                period === p.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`leaderboard-period-${p.id}`}
            >{p.label}</button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading leaderboard…
        </div>
      )}
      {error && !loading && (
        <div className="text-sm text-destructive py-6 text-center" data-testid="leaderboard-error">{error}</div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div className="text-sm text-muted-foreground py-6 text-center" data-testid="leaderboard-empty">
          No quotes sent in the last {period} days yet. The leaderboard fills in once your advisors start sending jobs.
        </div>
      )}
      {!loading && !error && rows.length > 0 && (
        <ul className="space-y-2" data-testid="leaderboard-rows">
          {rows.map((r, i) => (
            <li
              key={r.user_id || `none-${i}`}
              className="flex items-center gap-3 rounded-md border border-border bg-card/40 px-3 py-2.5"
              data-testid={`leaderboard-row-${i}`}
            >
              <span className={`w-7 h-7 rounded-full grid place-items-center font-display font-bold text-sm shrink-0 ${
                RANK_BG[i] || "bg-muted text-muted-foreground"
              }`}>
                {i === 0 ? <Award className="w-3.5 h-3.5" /> : `#${i + 1}`}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{r.name}</div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-2 mt-0.5 flex-wrap">
                  <span>{r.jobs_won} / {r.jobs_sent} jobs won</span>
                  {r.red_recovered > 0 && (
                    <>
                      <span className="opacity-30">·</span>
                      <span className="text-warning font-semibold">{r.red_recovered} red recovered</span>
                    </>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono font-bold text-sm text-success">{gbp(r.approved_value)}</div>
                <div className="flex items-center justify-end gap-1 text-[10px] text-muted-foreground">
                  <TrendingUp className="w-3 h-3" /> {r.conversion_rate}%
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Small footnote — feels honest, removes the "rigged"-ness directors sometimes worry about */}
      <p className="text-[10px] text-muted-foreground/60 mt-3 leading-relaxed">
        Ranked by approved-extras value first, then by conversion rate. Includes every quote sent in the period — drafts excluded.
      </p>
    </section>
  );
}
